import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Badge } from '@/shared/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/shared/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/components/ui/select';
import { Label } from '@/shared/components/ui/label';
import { Checkbox } from '@/shared/components/ui/checkbox';
import { Progress } from '@/shared/components/ui/progress';
import { toast } from 'sonner';
import {
  Play, Pause, RefreshCw, AlertTriangle, CheckCircle, XCircle,
  Clock, Upload, Loader2, Eye, MoreHorizontal, Calendar,
  Send, FileText, Download, AlertCircle, ChevronRight, Zap } from
'lucide-react';


export default function PublishingConsole() {const { t } = useTranslation("crm");
  const [activeTab, setActiveTab] = useState('jobs');
  const [jobs, setJobs] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [dlqJobs, setDlqJobs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showJobDetail, setShowJobDetail] = useState(false);
  const [filterState, setFilterState] = useState('all');
  const [filterMode, setFilterMode] = useState('all');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [jobsRes, schedulesRes, dlqRes, statsRes] = await Promise.all([
      fetch(`${API_BASE}/api/publish-jobs/jobs`),
      fetch(`${API_BASE}/api/publish-jobs/schedules`),
      fetch(`${API_BASE}/api/publish-jobs/dlq`),
      fetch(`${API_BASE}/api/publish-jobs/stats`)]
      );

      const jobsData = await jobsRes.json();
      const schedulesData = await schedulesRes.json();
      const dlqData = await dlqRes.json();
      const statsData = await statsRes.json();

      setJobs(Array.isArray(jobsData) ? jobsData : []);
      setSchedules(Array.isArray(schedulesData) ? schedulesData : []);
      setDlqJobs(Array.isArray(dlqData) ? dlqData : []);
      setStats(statsData);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load publishing data');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000); // Auto-refresh every 30s
    return () => clearInterval(interval);
  }, [fetchData]);

  const filteredJobs = jobs.filter((job) => {
    if (filterState !== 'all' && job.state !== filterState) return false;
    if (filterMode !== 'all' && job.mode !== filterMode) return false;
    return true;
  });

  const handleValidateJob = async (jobId) => {
    try {
      const res = await fetch(`${API_BASE}/api/publish-jobs/jobs/${jobId}/validate`, { method: 'POST' });
      const data = await res.json();
      if (data.valid) {
        toast.success('Job validated successfully');
      } else {
        toast.error(`Validation failed: ${data.errors?.length || 0} errors`);
      }
      fetchData();
    } catch (error) {
      toast.error('Validation failed');
    }
  };

  const handleActivateJob = async (jobId) => {
    try {
      const res = await fetch(`${API_BASE}/api/publish-jobs/jobs/${jobId}/activate`, { method: 'POST' });
      const data = await res.json();
      toast.success(data.message || 'Job activated');
      fetchData();
    } catch (error) {
      toast.error('Activation failed');
    }
  };

  const handleRetryJob = async (jobId) => {
    try {
      await fetch(`${API_BASE}/api/publish-jobs/jobs/${jobId}/retry`, { method: 'POST' });
      toast.success('Job queued for retry');
      fetchData();
    } catch (error) {
      toast.error('Retry failed');
    }
  };

  const handleCancelJob = async (jobId) => {
    if (!window.confirm('Are you sure you want to cancel this job?')) return;
    try {
      await fetch(`${API_BASE}/api/publish-jobs/jobs/${jobId}/cancel`, { method: 'POST' });
      toast.success('Job cancelled');
      fetchData();
    } catch (error) {
      toast.error('Failed to cancel job');
    }
  };

  const handleReplayAllDLQ = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/publish-jobs/dlq/replay-all`, { method: 'POST' });
      const data = await res.json();
      toast.success(data.message || 'DLQ jobs replayed');
      fetchData();
    } catch (error) {
      toast.error('Failed to replay DLQ');
    }
  };

  const getStateIcon = (state) => {
    const icons = {
      queued: <Clock className="w-4 h-4 text-gray-500" />,
      validating: <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />,
      ready: <CheckCircle className="w-4 h-4 text-green-500" />,
      activating: <Loader2 className="w-4 h-4 text-purple-500 animate-spin" />,
      active: <Play className="w-4 h-4 text-green-600" />,
      paused: <Pause className="w-4 h-4 text-yellow-500" />,
      completed: <CheckCircle className="w-4 h-4 text-blue-600" />,
      blocked_invalid_spec: <AlertTriangle className="w-4 h-4 text-orange-500" />,
      blocked_missing_cost: <AlertTriangle className="w-4 h-4 text-orange-500" />,
      needs_human_action: <AlertCircle className="w-4 h-4 text-yellow-600" />,
      failed: <XCircle className="w-4 h-4 text-red-500" />,
      cancelled: <XCircle className="w-4 h-4 text-gray-500" />
    };
    return icons[state] || <Clock className="w-4 h-4 text-gray-400" />;
  };

  const getStateBadge = (state) => {
    const variants = {
      queued: 'bg-gray-100 text-gray-700 border-gray-200',
      validating: 'bg-blue-100 text-blue-700 border-blue-200',
      ready: 'bg-green-100 text-green-700 border-green-200',
      activating: 'bg-purple-100 text-purple-700 border-purple-200',
      active: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      paused: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      completed: 'bg-blue-100 text-blue-700 border-blue-200',
      blocked_invalid_spec: 'bg-orange-100 text-orange-700 border-orange-200',
      blocked_missing_cost: 'bg-orange-100 text-orange-700 border-orange-200',
      needs_human_action: 'bg-amber-100 text-amber-700 border-amber-200',
      failed: 'bg-red-100 text-red-700 border-red-200',
      cancelled: 'bg-gray-100 text-gray-500 border-gray-200'
    };
    return (
      <Badge variant="outline" className={variants[state] || variants.queued}>
        {state?.replace(/_/g, ' ')}
      </Badge>);

  };

  const getModeBadge = (mode) => {
    const variants = {
      A: { bg: 'bg-green-500', label: 'API' },
      B: { bg: 'bg-blue-500', label: 'Direct IO' },
      C: { bg: 'bg-purple-500', label: 'Self-Serve' }
    };
    const v = variants[mode] || variants.C;
    return (
      <Badge className={`${v.bg} text-white`}>{t("mode", "Mode")}
        {mode}: {v.label}
      </Badge>);

  };

  const viewJobDetail = async (jobId) => {
    try {
      const res = await fetch(`${API_BASE}/api/publish-jobs/jobs/${jobId}`);
      const data = await res.json();
      setSelectedJob(data);
      setShowJobDetail(true);
    } catch (error) {
      toast.error('Failed to load job details');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>);

  }

  return (
    <div className="p-6 space-y-6" data-testid="publishing-console">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{t("publishingConsole", "Publishing Console")}</h1>
          <p className="text-sm text-gray-500 mt-1">{t("manageAndMonitor", "Manage and monitor campaign publishing jobs")}

          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 mr-2" />{t("refresh", "Refresh")}

          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats &&
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100/50 dark:from-blue-900/20 dark:to-blue-800/10">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">{t("totalJobs", "Total Jobs")}</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.total_jobs}</p>
                </div>
                <FileText className="w-8 h-8 text-blue-500/30" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-50 to-green-100/50 dark:from-green-900/20 dark:to-green-800/10">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">{t("active", "Active")}</p>
                  <p className="text-2xl font-bold text-green-600">{stats.by_state?.active || 0}</p>
                </div>
                <Play className="w-8 h-8 text-green-500/30" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-purple-50 to-purple-100/50 dark:from-purple-900/20 dark:to-purple-800/10">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">{t("ready", "Ready")}</p>
                  <p className="text-2xl font-bold text-purple-600">{stats.by_state?.ready || 0}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-purple-500/30" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-amber-50 to-amber-100/50 dark:from-amber-900/20 dark:to-amber-800/10">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">{t("blocked", "Blocked")}</p>
                  <p className="text-2xl font-bold text-amber-600">{stats.by_state?.blocked || 0}</p>
                </div>
                <AlertTriangle className="w-8 h-8 text-amber-500/30" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-red-50 to-red-100/50 dark:from-red-900/20 dark:to-red-800/10">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">{t("failed", "Failed")}</p>
                  <p className="text-2xl font-bold text-red-600">{stats.by_state?.failed || 0}</p>
                </div>
                <XCircle className="w-8 h-8 text-red-500/30" />
              </div>
            </CardContent>
          </Card>
        </div>
      }

      {/* Mode Distribution */}
      {stats &&
      <div className="flex items-center gap-6 px-4 py-2 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
          <span className="text-sm text-gray-500">{t("byMode", "By Mode:")}</span>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-green-500"></div>
            <span className="text-sm">{t("modeA", "Mode A:")}{stats.by_mode?.mode_a || 0}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-blue-500"></div>
            <span className="text-sm">{t("modeB", "Mode B:")}{stats.by_mode?.mode_b || 0}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-purple-500"></div>
            <span className="text-sm">{t("modeC", "Mode C:")}{stats.by_mode?.mode_c || 0}</span>
          </div>
        </div>
      }

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-gray-100 dark:bg-gray-800 p-1">
          <TabsTrigger value="jobs" className="data-[state=active]:bg-white dark:data-[state=active]:bg-gray-700">
            <FileText className="w-4 h-4 mr-2" />{t("publishJobs", "Publish Jobs")}

          </TabsTrigger>
          <TabsTrigger value="schedules" className="data-[state=active]:bg-white dark:data-[state=active]:bg-gray-700">
            <Calendar className="w-4 h-4 mr-2" />{t("schedules", "Schedules")}

          </TabsTrigger>
          <TabsTrigger value="dlq" className="data-[state=active]:bg-white dark:data-[state=active]:bg-gray-700">
            <AlertTriangle className="w-4 h-4 mr-2" />{t("dlq", "DLQ (")}
            {dlqJobs.length})
          </TabsTrigger>
        </TabsList>

        {/* Publish Jobs Tab */}
        <TabsContent value="jobs" className="space-y-4">
          {/* Filters */}
          <div className="flex items-center gap-4">
            <Select value={filterState} onValueChange={setFilterState}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Filter by state" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("allStates", "All States")}</SelectItem>
                <SelectItem value="queued">{t("queued", "Queued")}</SelectItem>
                <SelectItem value="ready">{t("ready", "Ready")}</SelectItem>
                <SelectItem value="active">{t("active", "Active")}</SelectItem>
                <SelectItem value="completed">{t("completed", "Completed")}</SelectItem>
                <SelectItem value="failed">{t("failed", "Failed")}</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterMode} onValueChange={setFilterMode}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Filter by mode" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("allModes", "All Modes")}</SelectItem>
                <SelectItem value="A">{t("modeAApi", "Mode A (API)")}</SelectItem>
                <SelectItem value="B">{t("modeBDirect", "Mode B (Direct IO)")}</SelectItem>
                <SelectItem value="C">{t("modeCSelfserve", "Mode C (Self-Serve)")}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Jobs Table */}
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="jobs-table">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400">{t("job", "Job")}</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400">{t("portal", "Portal")}</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400">{t("mode", "Mode")}</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400">{t("state", "State")}</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400">{t("schedule", "Schedule")}</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400">{t("externalId", "External ID")}</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-400">{t("actions", "Actions")}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {filteredJobs.map((job) =>
                    <tr key={job.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            {getStateIcon(job.state)}
                            <div>
                              <p className="font-medium text-gray-900 dark:text-white">#{job.id}</p>
                              <p className="text-xs text-gray-500">{job.creative_title || 'No creative'}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{job.portal_name}</td>
                        <td className="px-4 py-3">{getModeBadge(job.mode)}</td>
                        <td className="px-4 py-3">{getStateBadge(job.state)}</td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                          {job.start_datetime ? new Date(job.start_datetime).toLocaleDateString() : '-'}
                        </td>
                        <td className="px-4 py-3">
                          {job.external_campaign_id ?
                        <code className="text-xs bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
                              {job.external_campaign_id.substring(0, 15)}...
                            </code> :
                        '-'}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => viewJobDetail(job.id)}>
                              <Eye className="w-3.5 h-3.5" />
                            </Button>
                            {job.state === 'queued' &&
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-blue-600" onClick={() => handleValidateJob(job.id)}>
                                <CheckCircle className="w-3.5 h-3.5" />
                              </Button>
                          }
                            {job.state === 'ready' &&
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-green-600" onClick={() => handleActivateJob(job.id)}>
                                <Play className="w-3.5 h-3.5" />
                              </Button>
                          }
                            {job.state === 'failed' &&
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-amber-600" onClick={() => handleRetryJob(job.id)}>
                                <RefreshCw className="w-3.5 h-3.5" />
                              </Button>
                          }
                            {!['completed', 'cancelled', 'failed'].includes(job.state) &&
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => handleCancelJob(job.id)}>
                                <XCircle className="w-3.5 h-3.5" />
                              </Button>
                          }
                          </div>
                        </td>
                      </tr>
                    )}
                    {filteredJobs.length === 0 &&
                    <tr>
                        <td colSpan={7} className="px-4 py-12 text-center text-gray-500">
                          <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                          <p>{t("noPublishJobs", "No publish jobs found")}</p>
                          <p className="text-sm mt-1">{t("jobsWillAppear", "Jobs will appear here when campaigns are scheduled for publishing")}</p>
                        </td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Schedules Tab */}
        <TabsContent value="schedules" className="space-y-4">
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="schedules-table">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400">{t("schedule", "Schedule")}</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400">{t("portalPlacement", "Portal / Placement")}</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400">{t("creative", "Creative")}</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400">{t("start", "Start")}</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400">{t("end", "End")}</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400">{t("budget", "Budget")}</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400">{t("status", "Status")}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {schedules.map((schedule) =>
                    <tr key={schedule.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900 dark:text-white">#{schedule.id}</div>
                          <div className="text-xs text-gray-500">{t("campaign", "Campaign #")}{schedule.campaign_id}</div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm text-gray-900 dark:text-white">{schedule.portal_name}</div>
                          <div className="text-xs text-gray-500">{schedule.placement_name}</div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                          {schedule.creative_title || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                          {schedule.start_datetime ? new Date(schedule.start_datetime).toLocaleString() : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                          {schedule.end_datetime ? new Date(schedule.end_datetime).toLocaleString() : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                          {schedule.scheduled_budget ? `$${schedule.scheduled_budget.toLocaleString()}` : '-'}
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant="outline">{schedule.status || 'scheduled'}</Badge>
                        </td>
                      </tr>
                    )}
                    {schedules.length === 0 &&
                    <tr>
                        <td colSpan={7} className="px-4 py-12 text-center text-gray-500">
                          <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                          <p>{t("noSchedulesFound", "No schedules found")}</p>
                        </td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* DLQ Tab */}
        <TabsContent value="dlq" className="space-y-4">
          {dlqJobs.length > 0 &&
          <div className="flex justify-end">
              <Button variant="outline" onClick={handleReplayAllDLQ}>
                <RefreshCw className="w-4 h-4 mr-2" />{t("replayAllRetriable", "Replay All Retriable")}

            </Button>
            </div>
          }
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="dlq-table">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400">{t("job", "Job")}</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400">{t("portal", "Portal")}</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400">{t("mode", "Mode")}</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400">{t("state", "State")}</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400">{t("error", "Error")}</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400">{t("retries", "Retries")}</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-400">{t("actions", "Actions")}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {dlqJobs.map((job) =>
                    <tr key={job.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 bg-red-50/50 dark:bg-red-900/10">
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900 dark:text-white">#{job.id}</div>
                          <div className="text-xs text-gray-500">{job.creative_title || 'No creative'}</div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{job.portal_name}</td>
                        <td className="px-4 py-3">{getModeBadge(job.mode)}</td>
                        <td className="px-4 py-3">{getStateBadge(job.state)}</td>
                        <td className="px-4 py-3">
                          <p className="text-sm text-red-600 dark:text-red-400 max-w-xs truncate" title={job.error_message}>
                            {job.error_message || 'Unknown error'}
                          </p>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {job.retry_count}/{job.max_retries}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => viewJobDetail(job.id)}>
                              <Eye className="w-3.5 h-3.5" />
                            </Button>
                            {job.state === 'failed' && job.retry_count < job.max_retries &&
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-amber-600" onClick={() => handleRetryJob(job.id)}>
                                <RefreshCw className="w-3.5 h-3.5" />
                              </Button>
                          }
                          </div>
                        </td>
                      </tr>
                    )}
                    {dlqJobs.length === 0 &&
                    <tr>
                        <td colSpan={7} className="px-4 py-12 text-center text-gray-500">
                          <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-300" />
                          <p>{t("deadLetterQueue", "Dead Letter Queue is empty")}</p>
                          <p className="text-sm mt-1">{t("noFailedOr", "No failed or blocked jobs to review")}</p>
                        </td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Job Detail Dialog */}
      <JobDetailDialog
        open={showJobDetail}
        onClose={() => {setShowJobDetail(false);setSelectedJob(null);}}
        job={selectedJob}
        onValidate={handleValidateJob}
        onActivate={handleActivateJob}
        onRetry={handleRetryJob}
        onCancel={handleCancelJob} />
      
    </div>);

}

// Job Detail Dialog
function JobDetailDialog({ open, onClose, job, onValidate, onActivate, onRetry, onCancel }) {const { t } = useTranslation("crm");
  if (!job) return null;

  const getStateBadge = (state) => {
    const variants = {
      queued: 'bg-gray-100 text-gray-700',
      validating: 'bg-blue-100 text-blue-700',
      ready: 'bg-green-100 text-green-700',
      activating: 'bg-purple-100 text-purple-700',
      active: 'bg-emerald-100 text-emerald-700',
      completed: 'bg-blue-100 text-blue-700',
      failed: 'bg-red-100 text-red-700'
    };
    return (
      <Badge className={variants[state] || variants.queued}>
        {state?.replace(/_/g, ' ')}
      </Badge>);

  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">{t("publishJob", "Publish Job #")}
            {job.id}
            {getStateBadge(job.state)}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-gray-500">{t("portal", "Portal")}</Label>
              <p className="font-medium">{job.portal_name}</p>
            </div>
            <div>
              <Label className="text-xs text-gray-500">{t("mode", "Mode")}</Label>
              <p className="font-medium">{t("mode", "Mode")}{job.mode}</p>
            </div>
            <div>
              <Label className="text-xs text-gray-500">{t("creative", "Creative")}</Label>
              <p className="font-medium">{job.creative_title || 'Not assigned'}</p>
            </div>
            <div>
              <Label className="text-xs text-gray-500">{t("schedule", "Schedule")}</Label>
              <p className="font-medium">
                {job.start_datetime ? new Date(job.start_datetime).toLocaleDateString() : '-'}
                {job.end_datetime && ` - ${new Date(job.end_datetime).toLocaleDateString()}`}
              </p>
            </div>
          </div>

          {/* External IDs */}
          {(job.external_campaign_id || job.external_ad_id) &&
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
              <Label className="text-xs text-gray-500">{t("externalReferences", "External References")}</Label>
              <div className="mt-2 space-y-1">
                {job.external_campaign_id &&
              <p className="text-sm font-mono">{t("campaign", "Campaign:")}{job.external_campaign_id}</p>
              }
                {job.external_ad_id &&
              <p className="text-sm font-mono">{t("ad", "Ad:")}{job.external_ad_id}</p>
              }
              </div>
            </div>
          }

          {/* Error Details */}
          {job.error_message &&
          <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4">
              <Label className="text-xs text-red-600">{t("error", "Error")}</Label>
              <p className="mt-1 text-sm text-red-700">{job.error_message}</p>
              {job.error_details &&
            <pre className="mt-2 text-xs bg-red-100 dark:bg-red-900/30 p-2 rounded overflow-x-auto">
                  {JSON.stringify(job.error_details, null, 2)}
                </pre>
            }
            </div>
          }

          {/* Self-Serve Checklist */}
          {job.mode === 'C' && job.self_serve_checklist &&
          <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4">
              <Label className="text-xs text-purple-600 mb-3 block">{t("selfserveChecklist", "Self-Serve Checklist")}</Label>
              <div className="space-y-2">
                {(typeof job.self_serve_checklist === 'string' ? JSON.parse(job.self_serve_checklist) : job.self_serve_checklist).map((item, i) =>
              <div key={i} className="flex items-center gap-2">
                    <Checkbox checked={item.is_completed} disabled />
                    <span className={`text-sm ${item.is_completed ? 'text-gray-500 line-through' : ''}`}>
                      {item.step_name}
                    </span>
                  </div>
              )}
              </div>
            </div>
          }

          {/* State History */}
          {job.state_history && job.state_history.length > 0 &&
          <div>
              <Label className="text-xs text-gray-500 mb-2 block">{t("stateHistory", "State History")}</Label>
              <div className="space-y-2">
                {job.state_history.map((h, i) =>
              <div key={i} className="flex items-center gap-3 text-sm">
                    <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                    <span className="text-gray-500">{new Date(h.timestamp).toLocaleString()}</span>
                    <ChevronRight className="w-3 h-3 text-gray-400" />
                    <span>{h.transition?.from_state || '(start)'}</span>
                    <ChevronRight className="w-3 h-3 text-gray-400" />
                    <span className="font-medium">{h.transition?.to_state}</span>
                  </div>
              )}
              </div>
            </div>
          }
        </div>

        <DialogFooter className="mt-6">
          {job.state === 'queued' &&
          <Button onClick={() => {onValidate(job.id);onClose();}}>
              <CheckCircle className="w-4 h-4 mr-2" />{t("validate", "Validate")}

          </Button>
          }
          {job.state === 'ready' &&
          <Button onClick={() => {onActivate(job.id);onClose();}}>
              <Play className="w-4 h-4 mr-2" />{t("activate", "Activate")}

          </Button>
          }
          {job.state === 'failed' &&
          <Button variant="outline" onClick={() => {onRetry(job.id);onClose();}}>
              <RefreshCw className="w-4 h-4 mr-2" />{t("retry", "Retry")}

          </Button>
          }
          {!['completed', 'cancelled'].includes(job.state) &&
          <Button variant="destructive" onClick={() => {onCancel(job.id);onClose();}}>
              <XCircle className="w-4 h-4 mr-2" />{t("cancel", "Cancel")}

          </Button>
          }
          <Button variant="outline" onClick={onClose}>{t("close", "Close")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>);

}
